/**
 * Program Admin Dashboard - For Client Organizations
 * Copying best practices from Asana + Monday.com
 * 
 * This is what Program Admins see (NOT Platform Admins)
 * Limited to their own program(s) only
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Megaphone, FileText, BarChart3, Settings,
  UserPlus, Plus, Send, Eye, Edit, Trash2,
  TrendingUp, Award, Target, Calendar, Bell
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramAdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Program data
  const [program, setProgram] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    category: 'general',
    target_roles: ['youth']
  });
  
  // Documents/Resources
  const [resources, setResources] = useState([]);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    url: '',
    category: 'program_info',
    is_featured: false
  });
  
  // Users
  const [programUsers, setProgramUsers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'youth'
  });

  useEffect(() => {
    fetchProgramData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProgramData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Get user's program (program admins are linked to a specific program)
      const userRes = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (userRes.ok) {
        const userData = await userRes.json();
        const programId = userData.user.program_id;
        
        if (!programId) {
          toast.error('No program associated with your account');
          return;
        }
        
        // Fetch program details
        const [progRes, usersRes, announcementsRes, resourcesRes] = await Promise.all([
          fetch(`${API_URL}/api/platform/programs/${programId}`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/platform/programs/${programId}/users`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/announcements`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/resources`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        if (progRes.ok) {
          const progData = await progRes.json();
          setProgram(progData);
          
          // Compute stats
          const users = usersRes.ok ? await usersRes.json() : { users: [] };
          setProgramUsers(users.users || []);
          
          setStats({
            total_youth: users.users?.filter(u => u.role === 'youth').length || 0,
            total_staff: users.users?.filter(u => ['coach', 'lead_monitor'].includes(u.role)).length || 0,
            total_users: users.users?.length || 0
          });
        }
        
        if (announcementsRes.ok) {
          setAnnouncements(await announcementsRes.json());
        }
        
        if (resourcesRes.ok) {
          setResources(await resourcesRes.json());
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load program data');
    } finally {
      setLoading(false);
    }
  };
  
  const createAnnouncement = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: announcementForm.title,
          message: announcementForm.message,
          category: announcementForm.category,
          target_roles: announcementForm.target_roles,
          send_email: true
        })
      });
      
      if (response.ok) {
        toast.success('Announcement created and sent!');
        setShowAnnouncementModal(false);
        setAnnouncementForm({ title: '', message: '', category: 'general', target_roles: ['youth'] });
        fetchProgramData();
      } else {
        toast.error('Failed to create announcement');
      }
    } catch (error) {
      toast.error('Error creating announcement');
    }
  };
  
  const createResource = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/resources`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(resourceForm)
      });
      
      if (response.ok) {
        toast.success('Resource added!');
        setShowResourceModal(false);
        setResourceForm({ title: '', description: '', url: '', category: 'program_info', is_featured: false });
        fetchProgramData();
      } else {
        toast.error('Failed to add resource');
      }
    } catch (error) {
      toast.error('Error adding resource');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <Card className="p-8 text-center">
          <h2 className="text-xl font-bold mb-4" style={{ color: colors.text }}>No Program Access</h2>
          <p style={{ color: colors.text_secondary }}>Your account is not linked to a program.</p>
        </Card>
      </div>
    );
  }

          {/* Resources Tab */}
          <TabsContent value="resources">
            <ResourceManagement programId={program?.id} userRole="program_admin" />
          </TabsContent>

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div
        className="border-b sticky top-0 z-50 backdrop-blur-md"
        style={{
          backgroundColor: colors.header,
          borderColor: colors.border
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: colors.text_on_primary }}>
                {program.name} - Admin
              </h1>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                {program.city}, {program.state} · Program Management
              </p>
            </div>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              style={{ color: colors.text_on_primary }}
              className="hover:bg-white/10"
            >
              Switch to User View
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
                <Users className="w-6 h-6" style={{ color: '#22C55E' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: colors.text }}>{stats?.total_youth || 0}</p>
                <p className="text-xs font-semibold" style={{ color: colors.text }}>{/* Changed from text_muted */}Youth</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                <Users className="w-6 h-6" style={{ color: '#3B82F6' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: colors.text }}>{stats?.total_staff || 0}</p>
                <p className="text-xs font-semibold" style={{ color: colors.text }}>Staff</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(251,146,60,0.1)' }}>
                <Megaphone className="w-6 h-6" style={{ color: '#FB923C' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: colors.text }}>{announcements.length}</p>
                <p className="text-xs font-semibold" style={{ color: colors.text }}>Announcements</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(168,85,247,0.1)' }}>
                <FileText className="w-6 h-6" style={{ color: '#A855F7' }} />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ color: colors.text }}>{resources.length}</p>
                <p className="text-xs font-semibold" style={{ color: colors.text }}>Resources</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="border mb-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="announcements" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <Megaphone className="w-4 h-4 mr-2" />
              Announcements
            </TabsTrigger>
            <TabsTrigger value="resources" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <FileText className="w-4 h-4 mr-2" />
              Resources
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Quick Actions */}
              <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: colors.text }}>Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    onClick={() => setShowAnnouncementModal(true)}
                    className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Megaphone className="w-4 h-4 mr-2" />
                    Create Announcement
                  </Button>
                  <Button
                    onClick={() => setShowResourceModal(true)}
                    variant="outline"
                    className="w-full justify-start"
                    style={{ borderColor: colors.border, color: colors.text, backgroundColor: colors.card }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Resource
                  </Button>
                  <Button
                    onClick={() => setShowInviteModal(true)}
                    variant="outline"
                    className="w-full justify-start"
                    style={{ borderColor: colors.border, color: colors.text, backgroundColor: colors.card }}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Invite User
                  </Button>
                </div>
              </Card>
              
              {/* Program Info */}
              <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: colors.text }}>Program Information</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: colors.text }}>Program Name</p>
                    <p className="font-medium" style={{ color: colors.text }}>{program.name}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: colors.text }}>Location</p>
                    <p className="font-medium" style={{ color: colors.text }}>{program.city}, {program.state}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: colors.text }}>Current Season</p>
                    <p className="font-medium" style={{ color: colors.text }}>
                      {program.seasons?.[program.seasons.length - 1]?.season_name || '2026 Summer'}
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: colors.text }}>Announcements</h2>
                <Button onClick={() => setShowAnnouncementModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  New Announcement
                </Button>
              </div>

              {announcements.length === 0 ? (
                <Card className="p-12 text-center" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <Megaphone className="w-16 h-16 mx-auto mb-4" style={{ color: colors.text_secondary }} />
                  <h3 className="text-lg font-bold mb-2" style={{ color: colors.text }}>No Announcements Yet</h3>
                  <p style={{ color: colors.text_secondary }}>Create your first announcement to communicate with program participants.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {announcements.map(ann => (
                    <Card key={ann.id} className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-bold" style={{ color: colors.text }}>{ann.title}</h3>
                            <Badge className={ann.category === 'urgent' ? 'bg-red-500/20 text-red-600' : 'bg-blue-500/20 text-blue-600'}>
                              {ann.category}
                            </Badge>
                          </div>
                          <p className="text-sm" style={{ color: colors.text }}>{ann.message}</p>
                          <p className="text-xs mt-2" style={{ color: colors.text_secondary }}>
                            Sent to: {ann.target_roles?.join(', ')} · {new Date(ann.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Resources Tab */}
          <TabsContent value="resources">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: colors.text }}>Program Resources</h2>
                <Button onClick={() => setShowResourceModal(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Resource
                </Button>
              </div>

              {resources.length === 0 ? (
                <Card className="p-12 text-center" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: colors.text_secondary }} />
                  <h3 className="text-lg font-bold mb-2" style={{ color: colors.text }}>No Resources Yet</h3>
                  <p style={{ color: colors.text_secondary }}>Add important documents and resources for your program participants.</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {resources.map(res => (
                    <Card key={res.id} className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold" style={{ color: colors.text }}>{res.title}</h3>
                          <p className="text-sm mt-1" style={{ color: colors.text }}>{res.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => window.open(res.url, '_blank')} className="text-blue-600 hover:bg-blue-50">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: colors.text }}>Program Users</h2>
                <Button onClick={() => setShowInviteModal(true)} className="bg-green-600 hover:bg-green-700 text-white">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite User
                </Button>
              </div>

              <Card style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead style={{ backgroundColor: colors.surface }}>
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold" style={{ color: colors.text }}>Name</th>
                        <th className="text-left p-4 text-sm font-semibold" style={{ color: colors.text }}>Email</th>
                        <th className="text-left p-4 text-sm font-semibold" style={{ color: colors.text }}>Role</th>
                        <th className="text-left p-4 text-sm font-semibold" style={{ color: colors.text }}>Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {programUsers.map(u => (
                        <tr key={u.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{u.avatar_emoji || '😊'}</span>
                              <span className="font-medium" style={{ color: colors.text }}>{u.name}</span>
                            </div>
                          </td>
                          <td className="p-4 font-medium" style={{ color: colors.text }}>{u.email}</td>
                          <td className="p-4">
                            <Badge className="bg-blue-500/10 text-blue-600">{u.role}</Badge>
                          </td>
                          <td className="p-4 font-semibold" style={{ color: colors.text }}>{u.total_points || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Announcement Modal */}
      <Dialog open={showAnnouncementModal} onOpenChange={setShowAnnouncementModal}>
        <DialogContent className="max-w-2xl" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <DialogHeader>
            <DialogTitle style={{ color: colors.text }}>Create Announcement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label style={{ color: colors.text }}>Title</Label>
              <Input
                value={announcementForm.title}
                onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                placeholder="Announcement title"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              />
            </div>
            <div>
              <Label style={{ color: colors.text }}>Message</Label>
              <Textarea
                value={announcementForm.message}
                onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                placeholder="Write your message..."
                rows={5}
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              />
            </div>
            <div>
              <Label style={{ color: colors.text }}>Category</Label>
              <select
                value={announcementForm.category}
                onChange={(e) => setAnnouncementForm({...announcementForm, category: e.target.value})}
                className="w-full p-2 rounded-lg border"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              >
                <option value="general">General</option>
                <option value="urgent">Urgent</option>
                <option value="reminder">Reminder</option>
              </select>
            </div>
            <Button onClick={createAnnouncement} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              <Send className="w-4 h-4 mr-2" />
              Create & Send to All Youth
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Resource Modal */}
      <Dialog open={showResourceModal} onOpenChange={setShowResourceModal}>
        <DialogContent style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <DialogHeader>
            <DialogTitle style={{ color: colors.text }}>Add Resource</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label style={{ color: colors.text }}>Title</Label>
              <Input
                value={resourceForm.title}
                onChange={(e) => setResourceForm({...resourceForm, title: e.target.value})}
                placeholder="Resource title"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              />
            </div>
            <div>
              <Label style={{ color: colors.text }}>Description</Label>
              <Textarea
                value={resourceForm.description}
                onChange={(e) => setResourceForm({...resourceForm, description: e.target.value})}
                placeholder="Brief description"
                rows={3}
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              />
            </div>
            <div>
              <Label style={{ color: colors.text }}>URL</Label>
              <Input
                value={resourceForm.url}
                onChange={(e) => setResourceForm({...resourceForm, url: e.target.value})}
                placeholder="https://..."
                type="url"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              />
            </div>
            <Button onClick={createResource} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Add Resource
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite User Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <DialogHeader>
            <DialogTitle style={{ color: colors.text }}>Invite User to Program</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label style={{ color: colors.text }}>Name</Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                placeholder="Full name"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              />
            </div>
            <div>
              <Label style={{ color: colors.text }}>Email</Label>
              <Input
                value={inviteForm.email}
                onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                placeholder="user@example.com"
                type="email"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              />
            </div>
            <div>
              <Label style={{ color: colors.text }}>Role</Label>
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({...inviteForm, role: e.target.value})}
                className="w-full p-2 rounded-lg border"
                style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
              >
                <option value="youth">Youth</option>
                <option value="coach">Coach</option>
                <option value="lead_monitor">Lead Monitor</option>
                <option value="parent">Parent</option>
              </select>
            </div>
            <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
              <Send className="w-4 h-4 mr-2" />
              Send Invitation
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramAdminDashboard;
